import { IFeatureSection, ITierData, ITierHighlights } from "./types";
import { EPriceTabView } from "../../../../@types";
import {
  TIER_PRICING,
  TIER_STRIPE_LOOKUP,
  TIER_NAMES,
} from "MVVM/Utilities/SubscriptionTiers";
import { URLS } from "MVVM/Utilities/Maps";

export const TIER_HIGHLIGHTS: ITierHighlights = {
  FREE: {
    sections: [
      {
        title: "Clips & Quality",
        items: [
          "**5** clips per month",
          "**10** clips of cloud storage",
          "**720p** at **30fps** quality",
        ],
      },
      {
        title: "Features",
        items: ["Add music to your clips"],
      },
    ],
  },
  ESSENTIALS: {
    sections: [
      {
        title: "Clips & Quality",
        items: [
          "**10** clips per month",
          "**50** clips of cloud storage",
          "**1080p** at **60fps** for smooth playback",
        ],
      },
      {
        title: "Features",
        items: [
          "Download clips to your device",
          "Access to filters, effects & cameras",
          "Create squad, match and career montages",
        ],
      },
    ],
  },
  PRO: {
    sections: [
      {
        title: "Clips & Quality",
        items: [
          "**50** clips per month",
          "**600** clips of cloud storage",
          "**1080p** at **120fps** for buttery smooth clips",
        ],
      },
      {
        title: "Features",
        items: [
          "**Bulk download** all your clips at once",
          "**Timeline Editor** for precise control over clip creation",
          "Remove watermarks on your clips",
          "**Autosave** all your clips automatically",
        ],
      },
    ],
  },
  CREATOR_PLAT: {
    sections: [
      {
        title: "Clips & Quality",
        items: [
          "**Unlimited** clips, create as much as you want",
          "**Unlimited** cloud storage for all your content",
          "Stunning **4K** at up to **600fps**",
        ],
      },
      {
        title: "Features",
        items: [
          "**Priority** processing, skip the queue",
          "**Autosave** all your clips automatically",
          "Access to every feature from Standard",
        ],
      },
    ],
  },
};

export const AUTOCLIP_PATH = URLS.AUTOCAPTURE;
export const AUTOCLIP_FEATURE_NAME = "Autocapture Clips per Match";

// Its own section on the entitled cards: first line says what the feature does,
// second gives that tier's limit. Only tiers present in the backend's
// TIER_CLIP_CAPS are entitled, so anything absent here renders nothing.
export const TIER_AUTOCLIP: Record<string, string[]> = {
  PRO: [
    "Your best plays from every match, clipped automatically",
    "Up to **5** clips per match",
  ],
  CREATOR_PLAT: [
    "Your best plays from every match, clipped automatically",
    "**Every** highlight worth keeping, every match",
  ],
};

export const TIER_DATA: ITierData[] = [
  {
    key: "FREE",
    name: TIER_NAMES.FREE,
    description: "Get started creating and sharing clips",
    pricing: {
      monthly: "Free",
      monthlyAnnual: "Free",
      annual: "Free",
      annualTotal: "Free",
      discount: "",
    },
    stripeLookup: {
      [EPriceTabView.Monthly]: "",
      [EPriceTabView.Annual]: "",
    },
  },
  {
    key: "ESSENTIALS",
    name: TIER_NAMES.ESSENTIALS,
    description: "More clips, downloads, and extra features",
    pricing: TIER_PRICING.ESSENTIALS,
    stripeLookup: TIER_STRIPE_LOOKUP.ESSENTIALS,
  },
  {
    key: "PRO",
    name: TIER_NAMES.PRO,
    description: "Advanced editing for more serious creators",
    pricing: TIER_PRICING.PRO,
    stripeLookup: TIER_STRIPE_LOOKUP.PRO,
  },
  {
    key: "CREATOR_PLAT",
    name: TIER_NAMES.CREATOR_PLAT,
    description: "Unlimited clipping for professional creators",
    pricing: TIER_PRICING.CREATOR_PLAT,
    stripeLookup: TIER_STRIPE_LOOKUP.CREATOR_PLAT,
  },
];

export const FEATURE_SECTIONS: IFeatureSection[] = [
  {
    title: "Clips & Storage",
    features: [
      // Mirrors TIER_CLIP_CAPS on the backend: PRO/PRO_PLUS 5, CREATOR_PLAT 10.
      // Tiers absent from that map are not entitled to Autocapture at all.
      {
        name: AUTOCLIP_FEATURE_NAME,
        free: false,
        basic: false,
        standard: "5",
        unlimited: "Everything",
      },
      {
        name: "Clips per Month",
        free: "5",
        basic: "10",
        standard: "50",
        unlimited: "∞",
      },
      {
        name: "Cloud Storage",
        free: "10 Clips",
        basic: "50 Clips",
        standard: "600 Clips",
        unlimited: "∞",
      },
      {
        name: "Processing Speed",
        free: "Low",
        basic: "Standard",
        standard: "Fast",
        unlimited: "Priority",
      },
      {
        name: "Max Resolution",
        free: "720p",
        basic: "1080p",
        standard: "4K",
        unlimited: "4K",
      },
      {
        name: "Max Frame Rate",
        free: "30 fps",
        basic: "60 fps",
        standard: "120 fps",
        unlimited: "120 fps",
      },
    ],
  },
  {
    title: "Features",
    features: [
      {
        name: "Download Clips",
        free: false,
        basic: true,
        standard: true,
        unlimited: true,
      },
      {
        name: "Mobile Clips",
        free: false,
        basic: true,
        standard: true,
        unlimited: true,
      },
      {
        name: "Create Team",
        free: false,
        basic: true,
        standard: true,
        unlimited: true,
      },
      {
        name: "Username Change",
        free: false,
        basic: false,
        standard: true,
        unlimited: true,
      },
      {
        name: "Autosave Clips",
        free: false,
        basic: false,
        standard: true,
        unlimited: true,
      },
    ],
  },
  {
    title: "Editing",
    features: [
      {
        name: "Add Music",
        free: true,
        basic: true,
        standard: true,
        unlimited: true,
      },
      {
        name: "Filters & FX",
        free: false,
        basic: true,
        standard: true,
        unlimited: true,
      },
      {
        name: "Cameras",
        free: false,
        basic: true,
        standard: true,
        unlimited: true,
      },
      {
        name: "Time Remap",
        free: false,
        basic: true,
        standard: true,
        unlimited: true,
      },
      {
        name: "Enemy POV",
        free: false,
        basic: true,
        standard: true,
        unlimited: true,
      },
    ],
  },
  {
    title: "Tools",
    features: [
      {
        name: "Montages",
        free: false,
        basic: true,
        standard: true,
        unlimited: true,
      },
      {
        name: "Timeline Editor",
        free: false,
        basic: true,
        standard: true,
        unlimited: true,
      },
      {
        name: "Remove Watermark",
        free: false,
        basic: true,
        standard: true,
        unlimited: true,
      },
      {
        name: "Bulk Download Clips",
        free: false,
        basic: false,
        standard: true,
        unlimited: true,
      },
    ],
  },
];

export const getTierFeatureValue = (
  tierName: string,
  feature: { free: any; basic: any; standard: any; unlimited: any },
) => {
  switch (tierName.toLowerCase()) {
    case "free":
      return feature.free;
    case "basic":
      return feature.basic;
    case "standard":
      return feature.standard;
    case "unlimited":
      return feature.unlimited;
    default:
      return feature.free;
  }
};
